import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  useGetRequirementFeedbackStatsByStudentIdLazyQuery,
  useGetDataRequestFeedbackLazyQuery,
  useGetUserByIdLazyQuery,
  useGetValidFeedbacksByStudentIdLazyQuery,
  useGetValidFeedbacksByStudentAndCoachLazyQuery,
  useGetRequirementFeedbackStatsByStudentAndCoachLazyQuery,
} from "graphql/__generated__/hooks";
import { isFeedbackExpired, shouldHideFeedback } from "utils/feedback";
import { useAuthUser } from "hooks/useAuthUser";
import { ROLES, User } from "types/user";

type RequirementOption = { id: string; name: string };

export const useCustomUserDetail = () => {
  const { organizationId, userId } = useParams();
  const { user } = useAuthUser();

  const [selectedRequirementId, setSelectedRequirementId] =
    useState<string>("ALL");

  const [getStudentContext, { data: studentCtxData, loading: loadingCtx }] =
    useGetDataRequestFeedbackLazyQuery();
  const [getUserById, { data: userData, loading: loadingUser }] =
    useGetUserByIdLazyQuery();

  const userInfo = useMemo(() => {
    return userData?.user_by_pk as User;
  }, [userData]);

  const [getStats, { data: statsData, loading: loadingStats }] =
    useGetRequirementFeedbackStatsByStudentIdLazyQuery();
  const [getStatsByStudent, { data: statsByCoachData }] =
    useGetRequirementFeedbackStatsByStudentIdLazyQuery();
  const [getAllByStudent, { data: allByStudentData, loading: loadingAll }] =
    useGetValidFeedbacksByStudentIdLazyQuery();
  const [getAllByStudentAndCoach, { data: allByStudentAndCoachData }] =
    useGetValidFeedbacksByStudentAndCoachLazyQuery();

  const requirements: RequirementOption[] = useMemo(() => {
    const reqs =
      studentCtxData?.graduation_class_student?.[0]?.graduation_class
        ?.current_academic_year?.requirements || [];
    return reqs.map((r: any) => ({
      id: r.requirement.requirement_id,
      name: r.requirement.name,
    }));
  }, [studentCtxData]);

  const stats = useMemo(() => {
    const list =
      ((selectedRequirementId === "ALL"
        ? user?.role === ROLES.COACH
          ? allByStudentAndCoachData?.feedback
          : allByStudentData?.feedback
        : user?.role === ROLES.COACH
          ? statsByCoachData?.feedback
          : statsData?.feedback) ?? ([] as any[])
      ).filter((f: any) => !shouldHideFeedback(f));
    const total = list.length;
    const completed = list.filter((f: any) => f.status !== "PENDING").length;
    const accepted = list.filter((f: any) => f.status === "DONE").length;
    const pending = list.filter(
      (f: any) => !isFeedbackExpired(f) && f.status === "PENDING",
    ).length;
    const rejected = list.filter((f: any) => f.status === "DECLINED").length;
    const expired = list.filter(
      (f: any) =>
        isFeedbackExpired(f) &&
        f.status === "PENDING" &&
        !shouldHideFeedback(f),
    ).length;

    const avgResponseTime = (() => {
      const reviewedFeedbacks = list.filter((f: any) => f.reviewed_at);
      if (reviewedFeedbacks.length === 0) return 0;
      return (
        reviewedFeedbacks.reduce((acc: number, f: any) => {
          const createdAt = new Date(f.created_at || "");
          const reviewedAt = new Date(f.reviewed_at);
          return (
            acc + (reviewedAt.getTime() - createdAt.getTime()) / (1000 * 60)
          );
        }, 0) / reviewedFeedbacks.length
      );
    })();

    return {
      total,
      completed,
      accepted,
      pending,
      rejected,
      expired,
      avgResponseTime,
    };
  }, [
    statsData,
    statsByCoachData,
    allByStudentData,
    allByStudentAndCoachData,
    selectedRequirementId,
    user?.role,
  ]);

  const feedbacks = useMemo(() => {
    return (
      (selectedRequirementId === "ALL"
        ? user?.role === ROLES.COACH
          ? allByStudentAndCoachData?.feedback
          : allByStudentData?.feedback
        : user?.role === ROLES.COACH
          ? statsByCoachData?.feedback
          : statsData?.feedback) ?? ([] as any[])
    );
  }, [
    allByStudentData,
    allByStudentAndCoachData,
    statsData,
    statsByCoachData,
    selectedRequirementId,
    user?.role,
  ]);

  const requirementFeedbackStats = useMemo(() => {
    return selectedRequirementId !== "ALL"
      ? {
          totalFeedbacks: stats.total,
          currentRate: 0,
          data: (statsData?.feedback || []).map((f: any) => ({
            date: f.observation_date || f.created_at || "",
            value: f.coach_rate || 0,
            data: f,
          })),
        }
      : null;
  }, [statsData, allByStudentData, selectedRequirementId]);

  const effectiveOrganizationId = useMemo(() => {
    if (organizationId) return organizationId;
    if (user?.role === ROLES.COACH) {
      const gc = userData?.graduation_class_student?.[0]?.graduation_class;
      return (gc?.program as any)?.org_id || null;
    }
    return null;
  }, [organizationId, user?.role, userData]);

  const loadContext = useCallback(() => {
    if (!effectiveOrganizationId || !userId) return;
    getStudentContext({
      variables: { user_id: userId, org_id: effectiveOrganizationId },
    });
  }, [effectiveOrganizationId, userId]);

  const loadStats = useCallback(() => {
    if (!userId) return;
    if (selectedRequirementId === "ALL") {
      if (user?.role === ROLES.COACH && user.user_id) {
        getAllByStudentAndCoach({
          variables: { studentId: userId, coachId: user.user_id },
        });
      } else {
        getAllByStudent({ variables: { studentId: userId } });
      }
      return;
    }
    if (!selectedRequirementId) return;
    if (user?.role === ROLES.COACH && user.user_id) {
      getStatsByStudent({
        variables: {
          requirementId: selectedRequirementId,
          studentId: userId,
        },
      });
    } else {
      getStats({
        variables: { requirementId: selectedRequirementId, studentId: userId },
      });
    }
  }, [selectedRequirementId, userId, user?.role, user?.user_id]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  // Default remains "ALL"; do not override automatically when requirements load

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (userId) {
      getUserById({ variables: { userId } });
    }
  }, [userId]);

  return {
    loading: loadingCtx || loadingStats || loadingAll || loadingUser,
    requirements,
    selectedRequirementId,
    setSelectedRequirementId,
    stats,
    feedbacks,
    requirementFeedbackStats,
    userInfo,
    effectiveOrganizationId,

    onClickDot: (_: any) => {},
    onClickShowFeedbacks: () => {},
    role: user?.role,
  };
};
